// src/pages/home/questionnaire-models.js

export const questionModels = {
  // --------------------------------------------------
  // TEXT (with special handling for walkDuration)
  // --------------------------------------------------
  text: (q, saveFn) => {
    const wrapper = document.createElement("div");
    wrapper.className = "form-question";

    const label = document.createElement("label");
    label.textContent = q.label;
    label.setAttribute("for", q.id);

    const input = document.createElement("input");
    input.id = q.id;
    input.name = q.id;

    // ⭐ Special handling for the duration field
    if (q.id === "walkDuration") {
      input.type = "text"; // we handle validation ourselves
      input.placeholder = "Minutes (e.g., 20)";
      input.classList.add("walk-duration-input");

      const error = document.createElement("span");
      error.style.color = "#d10b0b";
      error.style.fontSize = "0.85rem";
      error.style.marginTop = "0.25rem";

      const validateAndSave = () => {
        const raw = input.value.trim();

        // Allow blank (means "no duration provided")
        if (raw === "") {
          error.textContent = "";
          saveFn("");
          return;
        }

        if (!/^\d+$/.test(raw)) {
          error.textContent =
            "Please enter a whole number of minutes (digits only).";
          saveFn("");
          return;
        }

        const minutes = parseInt(raw, 10);
        if (!Number.isFinite(minutes) || minutes <= 0) {
          error.textContent =
            "Please enter a whole number of minutes greater than 0.";
          saveFn("");
          return;
        }

        error.textContent = "";
        saveFn(String(minutes));
      };

      input.addEventListener("input", validateAndSave);

      wrapper.appendChild(label);
      wrapper.appendChild(input);
      wrapper.appendChild(error);
      return wrapper;
    }

    // 🔹 Default behavior for normal text questions
    input.type = "text";
    input.addEventListener("input", () => saveFn(input.value));

    wrapper.appendChild(label);
    wrapper.appendChild(input);
    return wrapper;
  },

  // --------------------------------------------------
  // TEXTAREA
  // --------------------------------------------------
  textarea: (q, saveFn) => {
    const wrapper = document.createElement("div");
    wrapper.className = "form-question";

    const label = document.createElement("label");
    label.textContent = q.label;
    label.setAttribute("for", q.id);

    const textarea = document.createElement("textarea");
    textarea.id = q.id;
    textarea.name = q.id;

    textarea.addEventListener("input", () => saveFn(textarea.value));

    wrapper.appendChild(label);
    wrapper.appendChild(textarea);
    return wrapper;
  },

  // --------------------------------------------------
  // SIMPLE SELECT (starts blank)
  // --------------------------------------------------
  select: (q, saveFn) => {
    const wrapper = document.createElement("div");
    wrapper.className = "form-question";

    const label = document.createElement("label");
    label.textContent = q.label;
    label.setAttribute("for", q.id);

    const select = document.createElement("select");
    select.id = q.id;
    select.name = q.id;

    // ⭐ blank initial option so “no choice yet” is possible
    const blank = document.createElement("option");
    blank.value = "";
    blank.textContent = "";
    select.appendChild(blank);

    q.options.forEach(option => {
      const opt = document.createElement("option");
      opt.value = option;
      opt.textContent = option;
      select.appendChild(opt);
    });

    select.addEventListener("change", () => saveFn(select.value));

    wrapper.appendChild(label);
    wrapper.appendChild(select);
    return wrapper;
  },

  /* ---------------------------------------------------------
    MULTISELECT WITH “OTHER” SUPPORT
  --------------------------------------------------------- */
  multiselect: (q, saveFn) => {
    const wrapper = document.createElement("fieldset");
    wrapper.className = "form-question";

    const legend = document.createElement("legend");
    legend.textContent = q.label;
    wrapper.appendChild(legend);

    const group = document.createElement("div");
    group.className = "checkbox-group";

    const currentValues = new Set();
    let otherText = "";
    let otherChecked = false;

    const emit = () => {
      let values = [...currentValues];

      // Remove literal "Other"
      values = values.filter(v => v !== "Other");

      // Add typed text if applicable
      if (otherChecked && otherText.trim() !== "") {
        values.push(otherText.trim());
      }

      saveFn(values);
    };

    q.options.forEach(option => {
      const div = document.createElement("div");

      const checkbox = document.createElement("input");
      checkbox.type = "checkbox";
      checkbox.name = q.id;
      checkbox.value = option;
      checkbox.id = `${q.id}_${option}`;

      const label = document.createElement("label");
      label.setAttribute("for", checkbox.id);

      // ⭐ Special case for Other
      if (option === "Other") {
        label.textContent = "Other:";

        const otherInput = document.createElement("input");
        otherInput.type = "text";
        otherInput.placeholder = "Please specify";
        otherInput.style.marginLeft = "8px";

        checkbox.addEventListener("change", () => {
          otherChecked = checkbox.checked;

          if (otherChecked) {
            currentValues.add("Other");
          } else {
            currentValues.delete("Other");
          }

          emit();
        });

        otherInput.addEventListener("input", e => {
          otherText = e.target.value;
          emit();
        });

        div.appendChild(checkbox);
        div.appendChild(label);
        div.appendChild(otherInput);
        group.appendChild(div);
        return;
      }

      // Normal options
      label.textContent = option;

      checkbox.addEventListener("change", () => {
        if (checkbox.checked) {
          currentValues.add(option);
        } else {
          currentValues.delete(option);
        }
        emit();
      });

      div.appendChild(checkbox);
      div.appendChild(label);
      group.appendChild(div);
    });

    wrapper.appendChild(group);
    return wrapper;
  },

  // --------------------------------------------------
  // MATRIX
  // --------------------------------------------------
  matrix: (q, saveFn) => {
    const wrapper = document.createElement("fieldset");
    wrapper.className = "form-question";

    const legend = document.createElement("legend");
    legend.textContent = q.label;
    wrapper.appendChild(legend);

    const table = document.createElement("table");
    table.style.borderCollapse = "collapse";
    table.style.marginTop = "0.5rem";

    const thead = document.createElement("thead");
    const headRow = document.createElement("tr");
    headRow.appendChild(document.createElement("th"));

    q.columns.forEach(col => {
      const th = document.createElement("th");
      th.textContent = col;
      th.style.padding = "0.5rem";
      headRow.appendChild(th);
    });

    thead.appendChild(headRow);
    table.appendChild(thead);

    const values = {};

    const tbody = document.createElement("tbody");
    q.rows.forEach(rowLabel => {
      const tr = document.createElement("tr");

      const rowHeader = document.createElement("td");
      rowHeader.textContent = rowLabel;
      rowHeader.style.padding = "0.5rem";
      tr.appendChild(rowHeader);

      q.columns.forEach(col => {
        const td = document.createElement("td");
        td.style.textAlign = "center";
        td.style.padding = "0.5rem";

        const input = document.createElement("input");
        input.type = "radio";
        input.name = `${q.id}_${rowLabel}`;
        input.value = col;

        input.addEventListener("change", () => {
          values[rowLabel] = col;
          saveFn(values);
        });

        td.appendChild(input);
        tr.appendChild(td);
      });

      tbody.appendChild(tr);
    });

    table.appendChild(tbody);
    wrapper.appendChild(table);

    return wrapper;
  },

  // --------------------------------------------------
  // BOOLEAN (Yes / No)
  // --------------------------------------------------
  boolean: (q, saveFn) => {
    const wrapper = document.createElement("fieldset");
    wrapper.className = "form-question";

    const legend = document.createElement("legend");
    legend.textContent = q.label;
    wrapper.appendChild(legend);

    const group = document.createElement("div");
    group.className = "radio-group";

    ["Yes", "No"].forEach(value => {
      const div = document.createElement("div");

      const input = document.createElement("input");
      input.type = "radio";
      input.name = q.id;
      input.value = value;
      input.id = `${q.id}_${value}`;

      input.addEventListener("change", () => saveFn(value));

      const label = document.createElement("label");
      label.textContent = value;
      label.setAttribute("for", input.id);

      div.appendChild(input);
      div.appendChild(label);
      group.appendChild(div);
    });

    wrapper.appendChild(group);
    return wrapper;
  },

  // --------------------------------------------------
  // SELECT WITH "OTHER" (starts blank)
  // --------------------------------------------------
  selectWithOther: (q, saveFn) => {
    const wrapper = document.createElement("div");
    wrapper.className = "form-question";

    const label = document.createElement("label");
    label.textContent = q.label;
    label.setAttribute("for", q.id);
    wrapper.appendChild(label);

    const select = document.createElement("select");
    select.name = q.id;
    select.id = q.id;

    // ⭐ blank first option
    const blank = document.createElement("option");
    blank.value = "";
    blank.textContent = "";
    select.appendChild(blank);

    q.options.forEach(option => {
      const opt = document.createElement("option");
      opt.value = option;
      opt.textContent = option;
      select.appendChild(opt);
    });

    const otherOpt = document.createElement("option");
    otherOpt.value = "Other";
    otherOpt.textContent = "Other";
    select.appendChild(otherOpt);

    const otherInput = document.createElement("input");
    otherInput.type = "text";
    otherInput.placeholder = "Please specify";
    otherInput.style.display = "none";
    otherInput.id = `${q.id}_other`;
    otherInput.name = `${q.id}_other`;

    select.addEventListener("change", () => {
      if (select.value === "Other") {
        otherInput.style.display = "block";
        saveFn(otherInput.value);
      } else {
        otherInput.style.display = "none";
        saveFn(select.value);
      }
    });

    otherInput.addEventListener("input", () => {
      if (select.value === "Other") {
        saveFn(otherInput.value);
      }
    });

    wrapper.appendChild(select);
    wrapper.appendChild(otherInput);
    return wrapper;
  },

  // --------------------------------------------------
  // SLIDER
  // --------------------------------------------------
  slider: (q, saveFn) => {
    const wrapper = document.createElement("div");
    wrapper.className = "form-question";

    const label = document.createElement("label");
    label.textContent = `${q.label} (${q.min}–${q.max})`;
    label.setAttribute("for", q.id);
    wrapper.appendChild(label);

    const input = document.createElement("input");
    input.type = "range";
    input.min = q.min;
    input.max = q.max;
    input.value = q.default || q.min;
    input.id = q.id;
    input.name = q.id;

    const display = document.createElement("span");
    display.textContent = input.value;
    display.className = "slider-label";

    input.addEventListener("input", () => {
      display.textContent = input.value;
      saveFn(input.value);
    });

    wrapper.appendChild(input);
    wrapper.appendChild(display);
    return wrapper;
  },

  // --------------------------------------------------
  // TIME (normal HTML time input)
  // --------------------------------------------------
  time: (q, saveFn) => {
    const wrapper = document.createElement("div");
    wrapper.className = "form-question";

    const label = document.createElement("label");
    label.textContent = q.label;
    label.setAttribute("for", q.id);

    const input = document.createElement("input");
    input.type = "time";
    input.name = q.id;
    input.id = q.id;

    input.addEventListener("input", () => saveFn(input.value));

    wrapper.appendChild(label);
    wrapper.appendChild(input);
    return wrapper;
  },
};
